<template>
  <div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.common.operation')}}</label>
            <multiselect v-model="operation" :max-height="200" :searchable="true"
                         placeholder=""
                         label="name" track-by="name" :options="processes"
                         @close="fetchData"
                         select-label="" deselect-label="" selected-label=""
                         :allow-empty="false"
            ></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.start_date')}}</label>
            <datepicker v-model="start_date"
                        lang="en"
                        placeholder=""
                        :width="170"
                        :first-day-of-week="1"
                        @input="fetchData"
            ></datepicker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.end_date')}}</label>
            <datepicker v-model="end_date"
                        lang="en"
                        placeholder=""
                        :width="170"
                        :first-day-of-week="1"
                        @input="fetchData"
            ></datepicker>
          </b-col>
        </b-row>
      </div>
    </div>
    <price-editing-modal></price-editing-modal>
    <package-score-modal></package-score-modal>
    <div>
      <b-card no-body>
        <b-tabs card>
          <b-tab active @click="setGridData(fixedPriceGrid, fixed_prices)">
            <template slot="title">
              <portal-target name="fixed-price-tab"></portal-target>
            </template>
            <portal to="fixed-price-tab">
              {{$t('tabs.operations.fixed_price')}}
            </portal>
            <b-row>
              <b-col class="d-flex justify-content-end">
                <div class="d-flex">
                  <div class="button-group">
                    <b-btn variant="outline-success" size="sm"
                           @click="openPriceEditingModal('fixed_prices', {isNew: true})"
                           :disabled="!operation">
                      {{$t('buttons.common.add')}}
                    </b-btn>
                    <b-btn variant="outline-danger" size="sm" @click="deleteFixedPrice"
                           :disabled="!operation">
                      {{$t('buttons.common.delete')}}
                    </b-btn>
                  </div>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="fixedPriceGrid"
                             :columnDefs="priceColumns" :rowData="fixed_prices"
                             @rowDoubleClicked="loadFixedPriceData"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab @click="setGridData(operationCostGrid, process_prices)">
            <template slot="title">
              <portal-target name="operation-cost-tab"></portal-target>
            </template>
            <portal to="operation-cost-tab">
              {{$t('tabs.operations.operations_cost')}}
            </portal>
            <b-row>
              <b-col class="d-flex justify-content-end">
                <div class="d-flex">
                  <div class="button-group">
                    <b-btn variant="outline-success" size="sm"
                           @click="openPriceEditingModal('process_prices', {isNew: true})"
                           :disabled="!operation">
                      {{$t('buttons.common.add')}}
                    </b-btn>
                    <b-btn variant="outline-danger" size="sm"
                           @click="deleteProcessPrice"
                           :disabled="!operation">
                      {{$t('buttons.common.delete')}}
                    </b-btn>
                  </div>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="operationCostGrid"
                             :columnDefs="priceColumns" :rowData="process_prices"
                             @rowDoubleClicked="loadOperationCostData"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab @click="setGridData(scoresGrid, packages_scores)">
            <template slot="title">
              <portal-target name="scores-tab"></portal-target>
            </template>
            <portal to="scores-tab">
              {{$t('tabs.operations.scores')}}
            </portal>
            <b-row>
              <b-col class="d-flex align-items-center">
                <div class="d-flex align-items-center">
                  <input type="checkbox" v-model="isCompletedScores" @change="filterScores" id="show-completed-scores">
                  <label class="m-0 ml-2"
                         for="show-completed-scores">{{$t('labels.common.show_completed_scores')}}</label>
                </div>
              </b-col>
              <b-col class="d-flex justify-content-end">
                <div class="d-flex">
                  <div class="button-group">
                    <b-btn variant="outline-success" size="sm"
                           @click="openPackageScoreModal">
                      {{$t('buttons.common.add')}}
                    </b-btn>
                    <b-btn variant="outline-danger" size="sm" @click="deletePackageScore">
                      {{$t('buttons.common.delete')}}
                    </b-btn>
                  </div>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="scoresGrid"
                             :columnDefs="scoreColumns" :rowData="packages_scores"
                             @rowDoubleClicked="loadPackageScoreData"
                             @rowDataChanged="filterScores"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';
  import { AgGridVue } from 'ag-grid-vue';
  import Datepicker from 'vue2-datepicker';
  import Multiselect from 'vue-multiselect';
  import moment from 'moment';
  import {
    formatNumber,
    alignForNumber,
    autoResize,
  } from '@/helpers/renderHelpers';

  import { setNotifyData, handlerResponse } from '@/helpers/commonHelpers';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { translateColumns } from '@/helpers/translate-helper';

  import PriceEditingModal from '@/components/modals/PriceEditingModal';
  import PackageScoreModal from '@/components/modals/PackageScoreModal';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    components: {
      AgGridVue,
      Datepicker,
      Multiselect,
      PriceEditingModal,
      PackageScoreModal
    },
    data() {
      return {
        start_date: null,
        end_date: null,
        operation: null,
        isCompletedScores: false,
        gridOptions: {
          animateRows: true,
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              return formatNumber(params.value, null);
            },
            cellStyle(params) {
              return alignForNumber(params.value);
            }
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
          onGridReady() {
            if (!this.rowData.length) {
              this.api.showNoRowsOverlay();
            }
          }
        },
        fixedCostGrid: null,
        operationCostGrid: null,
        scoresGrid: null,
        priceSearchData: null
      }
    },
    methods: {
      fetchData() {
        this.priceSearchData = this.preparePriceSearchData();
        if (this.operation) {
          this.$store.dispatch('woodware/loadFixedPrices', this.priceSearchData);
          this.$store.dispatch('woodware/loadProcessPrices', this.priceSearchData);
        }
        this.$store.dispatch('woodware/loadPackagesScores', this.priceSearchData);
      },
      preparePriceSearchData() {
        return {
          process: this.operation && this.operation.id,
          start_date: this.start_date ? moment(this.start_date).format(DATE_FORMAT) : '',
          end_date: this.end_date ? moment(this.end_date).format(DATE_FORMAT) : '',
          movement_isnull: true
        }
      },
      setGridData(grid, data) {
        grid.api.setRowData(data);
      },
      openPriceEditingModal(type, priceData) {
        const additionalPriceData = {...this.priceSearchData};
        if (priceData.isNew) {
          priceData.start_date = this.start_date;
          priceData.end_date = this.end_date;
        }
        this.$root.$emit('openPriceEditingModal', {additionalPriceData, type, priceData});
      },
      openPackageScoreModal(scoreData = {}) {
        const additionalData = {...this.priceSearchData};
        if (!scoreData.id) {
          scoreData.start_date = this.start_date;
          scoreData.end_date = this.end_date;
        }
        this.$root.$emit('openPackageScoreModal', {additionalData, scoreData});
      },
      deleteFixedPrice() {
        const selectedRows = this.fixedPriceGrid.api.getSelectedRows();
        if (selectedRows.length) {
          const selectedPrice = selectedRows[0].id;
          this.$http.delete(`woodware/prices/fixed_prices/${selectedPrice}/`).then(() => {
            this.$notify(setNotifyData('Success', 'Fixed price has been deleted', 'success'));
            this.$store.dispatch('woodware/loadFixedPrices', this.priceSearchData);
          }).catch(e => notificationsRenderer(e.data, 'error'));;
        } else {
          this.$notify(setNotifyData('Select fixed price for removing', '', 'warn'));
        }
      },
      deleteProcessPrice() {
        const selectedRows = this.operationCostGrid.api.getSelectedRows();
        if (selectedRows.length) {
          const selectedPrice = selectedRows[0].id;
          this.$http.delete(`woodware/prices/process_prices/${selectedPrice}/`).then(() => {
            this.$notify(setNotifyData('Success', 'Operation cost has been deleted', 'success'));
            this.$store.dispatch('woodware/loadProcessPrices', this.priceSearchData);
          }).catch(e => notificationsRenderer(e.data, 'error'));;
        } else {
          this.$notify(setNotifyData('Select operation cost for removing', '', 'warn'));
        }
      },
      deletePackageScore() {
        const selectedRows = this.scoresGrid.api.getSelectedRows();
        if (selectedRows.length) {
          const selectedScore = selectedRows[0].id;
          this.$http.delete(`woodware/prices/packages_scores/${selectedScore}/`).then(() => {
            this.$notify(setNotifyData('Success', 'Package score has been deleted', 'success'));
            this.$store.dispatch('woodware/loadPackagesScores', this.priceSearchData);
          }).catch(e => notificationsRenderer(e.data, 'error'));
        } else {
          this.$notify(setNotifyData('Select package score for removing', '', 'warn'));
        }
      },
      async loadFixedPriceData(params) {
        const priceId = params.data.id;
        const priceData = await this.$http.get(`woodware/prices/fixed_prices/${priceId}/`).then(handlerResponse);
        priceData.process = priceData.process.id;
        this.openPriceEditingModal('fixed_prices', priceData);
      },
      async loadOperationCostData(params) {
        const priceId = params.data.id;
        const priceData = await this.$http.get(`woodware/prices/process_prices/${priceId}/`).then(handlerResponse);
        priceData.process = priceData.process.id;
        this.openPriceEditingModal('process_prices', priceData);
      },
      async loadPackageScoreData(params) {
        const scoreId = params.data.id;
        const priceData = await this.$http.get(`woodware/prices/packages_scores/${scoreId}/`).then(handlerResponse);
        this.openPackageScoreModal(priceData);
      },
      filterScores() {
        const filterComponent = this.scoresGrid.api.getFilterInstance('end_date');
        filterComponent.selectNothing();
        let model = [];
        const currentDate = new Date();
        if (!this.isCompletedScores) {
          // TODO: it might not be 'valueModel' instead of 'model'. not totally sure yet.
          model = filterComponent.valueModel.allUniqueValues.filter(date => {
            if (date !== null) {
              return (moment(date).format(DATE_FORMAT) >= moment(currentDate).format(DATE_FORMAT));
            } else {
              return true;
            }
          })
        } else {
          model = filterComponent.valueModel.allUniqueValues;
        }
        filterComponent.setModel(model);
        this.scoresGrid.api.onFilterChanged();
        localStorage.setItem('is-completed-scores', this.isCompletedScores);
      }
    },
    computed: {
      ...mapState('woodware', ['process_prices', 'fixed_prices', 'packages_scores']),
      ...mapGetters('woodware', ['processes']),
      ...mapState('pricePolicy', ['priceColumns', 'scoreColumns']),
      gridHeight() {
        return {height: 'calc(100vh - 300px)'};
      }
    },
    beforeMount() {
      this.fixedPriceGrid = {...this.gridOptions};
      this.operationCostGrid = {...this.gridOptions};
      this.scoresGrid = {...this.gridOptions};
      this.$store.cache.dispatch('woodware/getProcesses');
      this.$store.dispatch('woodware/loadPackagesScores', this.priceSearchData);
    },
    mounted() {
      if (localStorage.getItem('is-completed-scores')) {
        this.isCompletedScores = localStorage.getItem('is-completed-scores') === 'true';
      }
    },
    created() {
      [this.priceColumns, this.scoreColumns].forEach(columnsList => {
        translateColumns.call(this, columnsList);
      })
    },
    beforeDestroy() {
      this.$store.commit('woodware/setProcessPrices', []);
      this.$store.commit('woodware/setFixedPrices', []);
      this.$store.commit('woodware/setPackagesScores', []);
    }
  }
</script>

<style scoped>
  .grid {
    height: 500px;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/general/PricePolicy.vue